import { useCallback, type ReactNode } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Button } from '.';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useCarouselScrollState } from '@/shared/hooks/useCarouselScrollState';

interface CarouselProps<T> {
    items: T[];
    renderItem: (item: T, index: number) => ReactNode;
    keyExtractor?: (item: T, index: number) => string | number;
}

export function Carousel<T>({ items, renderItem, keyExtractor }: CarouselProps<T>) {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
    const { canScrollPrev, canScrollNext } = useCarouselScrollState(emblaApi);
    const hasOverflow = canScrollPrev || canScrollNext;

    const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

    return (
        <div className="relative">
            <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex">
                    {items.map((item, index) => (
                        <div
                            className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.3333%] p-2"
                            key={keyExtractor ? keyExtractor(item, index) : index}
                        >
                            {renderItem(item, index)}
                        </div>
                    ))}
                </div>
            </div>
            {hasOverflow && (
                <>
                    <Button
                        type="button"
                        onClick={scrollPrev}
                        disabled={!canScrollPrev}
                        ariaLabel="Previous"
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8"
                        variant="outline"
                        size="xs"
                        startIcon={<ChevronLeftIcon size={20} />}
                    />
                    <Button
                        type="button"
                        onClick={scrollNext}
                        disabled={!canScrollNext}
                        ariaLabel="Next"
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8"
                        variant="outline"
                        size="xs"
                        endIcon={<ChevronRightIcon size={20} />}
                    />
                </>
            )}
        </div>
    );
}
